import React from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

// Course review data (matching CourseReviewsPage)
const courseReviews = [
  {
    id: 1,
    slug: 'the-crossvines-family-tradition',
    title: 'The Crossvines: A Family Tradition for Over a Decade',
    date: 'April 19, 2025',
    image: 'https://images.pexels.com/photos/7242696/pexels-photo-7242696.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: '"My grandson Colin and I have been playing this beautiful course every week for over 10 years. The staff knows us by name, and watching him grow from a beginner to nearly beating his grandfather has been the joy of my life. The course\'s varied terrain and excellent maintenance make each round a new adventure, even after hundreds of visits."',
    author: 'Bob Cope',
    authorTitle: 'Member since 2015',
    content: `
      <p>"My grandson Colin and I have been playing this beautiful course every week for over 10 years. The staff knows us by name, and watching him grow from a beginner to nearly beating his grandfather has been the joy of my life. The course's varied terrain and excellent maintenance make each round a new adventure, even after hundreds of visits."</p>

      <div style="text-align: center; margin: 30px 0;">
        <video autoplay>
        <source src="https://media.istockphoto.com/id/1281498137/video/asian-chinese-active-senior-grandfather-golfer-giving-high-five-to-his-grandson-to-celebrate.mp4?s=mp4-640x640-is&k=20&c=gXvQH2MkpXCJAc4XgFqGIcVRSwzCxKMpflhmBNct2LE=" type="video/mp4">
        Your browser does not support the video tag.
        </video>
      </div>
      
      <h2>A Special Bond Formed on the Fairways</h2>
      
      <p>What began as simple golf lessons for my 12-year-old grandson has evolved into a cherished weekly tradition that's spanned more than a decade. Through high school championships, college applications, and now his early career, our Saturday morning tee time at The Crossvines has remained sacred.</p>

      <p>I still remember our first visit back in the spring of 2015. Colin had expressed interest in trying golf after watching the Masters on television, and I couldn't have been more excited to share my lifelong passion with him. We arrived early that Saturday morning, the dew still fresh on the grass, and the head pro Mark greeted us with warm enthusiasm.</p>

      <p>"Colin was beyond nervous that first day," I recall with a chuckle. "He was so worried about holding up play or embarrassing himself. I reminded him that every golfer starts somewhere, and The Crossvines was the perfect place for beginners and seasoned players alike."</p>

      <p>That first round was nothing short of comedic - Colin's drives barely made it past the ladies' tees, and his putting technique needed significant refinement. But there was a moment on the 8th hole when he struck a perfect 7-iron that sailed gracefully onto the green. The look of pure joy and accomplishment on his face cemented what would become our decade-long tradition.</p>

      <p>"The course has become our second home," says Colin, now 22 and an accomplished golfer in his own right. "Some of my most important life conversations with my grandfather happened walking down these fairways. From college decisions to career advice, heartbreaks to celebrations - our rounds at The Crossvines have seen it all."</p>

      <div style="text-align: center; margin: 30px 0;">
        <img src="https://photos.bluegolf.com/d9/c9/c6/e3/a53e401a89b42b21dae73440_l.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Aerial view of The Crossvines golf course" style="max-width: 100%; border-radius: 8px;">
        <p style="font-style: italic; margin-top: 10px;">Course layout of The Crossvines showing all 18 challenging holes</p>
      </div>
      
      <h2>Milestones on the Greens</h2>

      <p>Over our decade of play, we've celebrated countless milestones together. In 2017, Colin broke 90 for the first time, and I surprised him with a new set of Titleist irons to commemorate the achievement. Two years later, during his senior year of high school, he finally out-drove me consistently - a bittersweet moment for this proud grandfather.</p>

      <p>"The greatest moment had to be Colin's hole-in-one on the 12th," I beam with pride. "It was his 18th birthday, and the weather had been threatening rain all day. We debated cutting our round short, but Colin insisted we play at least through the signature 12th hole."</p>

      <p>The 12th at The Crossvines is a picturesque par-3 measuring 165 yards from the blue tees, with water protecting the front and right side of an undulating green. Colin selected his 7-iron, took a smooth swing, and the ball sailed high against the darkening sky.</p>

      <p>"It landed about 10 feet short of the pin, took two perfect bounces, and rolled straight into the cup," Colin recounts, still visibly excited years later. "The groundskeeper was nearby and witnessed the whole thing. By the time we reached the clubhouse, they had already heard about it and were waiting with champagne - sparkling cider for me, since I wasn't quite of age."</p>

      <div style="text-align: center; margin: 30px 0;">
        <img src="https://images.pexels.com/photos/164250/pexels-photo-164250.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Signature par-3 12th hole" style="max-width: 100%; border-radius: 8px;">
        <p style="font-style: italic; margin-top: 10px;">The signature par-3 12th hole where Colin scored his memorable hole-in-one</p>
      </div>
      
      <h2>Course Highlights: A Detailed Tour</h2>
      
      <p>The Crossvines offers nearly 7,000 yards of championship golf across 380 beautiful acres in Maryland's Potomac Valley. The front nine features more open play with strategic bunkering, while the back nine winds through mature woodlands with elevation changes that challenge shot selection.</p>

      <p>The opening hole provides a gentle handshake - a straightaway par-4 with a generously wide fairway that allows players to shake off any first-tee jitters. As you progress through the front nine, the course gradually reveals its character, with the par-5 4th hole offering the first real risk-reward decision as it doglegs around a shimmering pond.</p>

      <p>"We've developed our own little traditions for certain holes," explains Colin. "On the elevated 6th tee, we always take a moment to appreciate the view of the entire front nine stretched below. And on the 9th green, we have our mid-round snack - always peanut butter crackers and an apple, just like our very first round."</p>
      
      <ul>
        <li><strong>Signature Hole</strong>: The par-3 12th requires a precise tee shot over water to a well-protected green, with the Potomac Valley providing a stunning backdrop.</li>
        <li><strong>Most Challenging</strong>: The dogleg par-4 7th demands both length and accuracy, with trouble lurking on both sides of a narrow landing area.</li>
        <li><strong>Hidden Gem</strong>: The par-5 15th offers risk/reward opportunities for big hitters, with a second shot that can reach the green if you're brave enough to carry the creek.</li>
        <li><strong>Favorite Finishing Hole</strong>: The par-4 18th provides a dramatic conclusion, playing downhill toward the clubhouse with the patio full of spectators.</li>
      </ul>

      <p>The course changes character with the seasons, which is another reason we've never tired of our weekly rounds. Spring brings dogwoods and azaleas in full bloom along the 13th and 14th holes. Summer offers lush, green conditions with the rough at its most penalizing. Fall transforms the back nine woodland holes into a kaleidoscope of reds, oranges, and yellows. Even winter rounds have their charm, with faster fairways and crystal-clear views across the property.</p>
      
      <div style="text-align: center; margin: 30px 0;">
        <img src="https://visitmontgomery.com/wp-content/uploads/2023/07/The-Crossvines-WEB-1-768x364.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="The Crossvines Clubhouse Patio" style="max-width: 100%; border-radius: 8px;">
        <p style="font-style: italic; margin-top: 10px;">The clubhouse patio overlooking the 18th green - Bob and Colin's favorite post-round spot</p>
      </div>
      
      <h2>Beyond the Course: The Complete Experience</h2>
      
      <p>What sets The Crossvines apart isn't just the quality of golf but the complete experience. The clubhouse features The Farmhouse Bistro, serving exceptional farm-to-table cuisine with ingredients sourced from local Maryland farms. The unique wine tasting facility, showcasing vintages from the on-site vineyard, adds another dimension to post-round relaxation.</p>

      <p>"After our round, Colin and I usually enjoy lunch on the patio overlooking the 18th green," Bob explains. "We've celebrated birthdays, graduations, and even Colin's job offers at this very spot. The staff always remembers our usual orders - the turkey club for me and the Crossvines burger for Colin."</p>

      <p>The relationships formed over the years with staff members have become an integral part of our Crossvines experience. From Mark the head pro who gave Colin his first lessons, to Sarah at the front desk who always has our tee time ready, to Carlos the beverage cart operator who knows exactly when we'll be making the turn - these personal connections have made us feel like family.</p>

      <h2>Multigenerational Impact</h2>

      <p>We're not the only family to form deep bonds at The Crossvines. Over the years, we've noticed several other multigenerational groups making their own traditions. The Johnsons play every Father's Day with three generations now participating. The Williams sisters bring their father for his birthday each year. The Martinezes have a Christmas Eve morning round that's become legendary at the club.</p>

      <p>"There's something special about a golf course that can bring generations together," notes Colin. "My friends are always amazed at how close my grandfather and I are, and I credit these weekly rounds for building our relationship."</p>

      <p>Last summer, Colin brought his girlfriend Maria to join us for a round. Watching him patiently teach her the basics, just as I had taught him a decade earlier, brought tears to my eyes. The cycle continues, and I can only hope that someday Colin will bring his own children or grandchildren to The Crossvines.</p>
      
      <div style="text-align: center; margin: 30px 0;">
        <img src="https://storage.googleapis.com/foretee-cdn/course/2003796940.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Sunset at the 18th hole" style="max-width: 100%; border-radius: 8px;">
        <p style="font-style: italic; margin-top: 10px;">A spectacular sunset over the 18th green - the perfect conclusion to hundreds of memorable rounds</p>
      </div>
      
      <h2>Our 500th Round Celebration</h2>
      
      <p>This spring will mark our 500th round together at The Crossvines, a milestone we've been tracking for years. The club management, upon learning of this achievement, has offered to host a small celebration in our honor.</p>

      <p>"We're planning to invite friends and family who have heard our endless Crossvines stories over the years," Colin shares excitedly. "Grandfather has even commissioned a special commemorative scorecard that tracks our improvement over the decade - from my first round of 138 to my current average of 78."</p>
      
      <h2>Final Thoughts: More Than Just Golf</h2>
      
      <p>For anyone looking to create lasting memories with family while enjoying a top-quality golf experience, The Crossvines offers the perfect setting. The course's combination of challenging play, beautiful scenery, and welcoming atmosphere has created the backdrop for our family's most cherished traditions.</p>

      <p>"I've played courses across the country," I reflect, "from Pebble Beach to Pinehurst, but none have the personal significance of The Crossvines. It's where I watched my grandson grow from a boy into a man, one hole at a time."</p>
      
      <p>As Bob likes to say, "Golf is just the excuse - it's really about the time together. And there's no better place for that than The Crossvines."</p>
    `
  },
  {
    id: 2,
    slug: 'st-andrews-links-footsteps-of-champions',
    title: 'St. Andrews Links: Walking in the Footsteps of Champions',
    date: 'April 18, 2025',
    image: 'https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Our expert review of the historic St. Andrews Links, including detailed hole-by-hole analysis and tips for first-time visitors.',
    author: 'Tom Richards',
    authorTitle: 'Golf Historian',
    content: `
      <p>There are golf courses, and then there is St. Andrews Links - the birthplace of the game and a pilgrimage destination for golfers worldwide. Walking the hallowed fairways of the Old Course is a transcendent experience that connects you to over 600 years of golfing history.</p>
      
      <h2>The Old Course Experience</h2>
      
      <p>From the iconic Swilcan Bridge to the massive double greens and the notorious Hell Bunker, every step at St. Andrews feels significant. The course layout, largely unchanged for centuries, reminds us that the best designs work with the natural landscape rather than against it.</p>
      
      <p>The key to scoring well here isn't power but strategy and imagination. The open fairways can seem generous, but proper positioning is essential for approaching the large, undulating greens. Local knowledge is invaluable, making a caddie a worthwhile investment for first-time visitors.</p>
      
      <h2>Beyond the Old Course</h2>
      
      <p>While the Old Course rightfully captures the imagination, St. Andrews offers six additional courses worth exploring:</p>
      
      <ul>
        <li><strong>The New Course</strong>: Despite its name, it dates to 1895 and offers a more conventional layout</li>
        <li><strong>The Castle Course</strong>: The newest addition, featuring dramatic clifftop holes with stunning sea views</li>
        <li><strong>Jubilee, Eden, and Strathtyrum</strong>: Each with its own character and challenges</li>
      </ul>
      
      <h2>Tips for First-Time Visitors</h2>
      
      <p>Securing a tee time on the Old Course requires planning. The advance application process opens roughly a year ahead, but there's also a daily ballot for next-day tee times, and single golfers can often join with other groups through the starter.</p>
      
      <p>Beyond golf, the St. Andrews experience includes:</p>
      
      <ul>
        <li>Touring the British Golf Museum</li>
        <li>Walking the historic town with its university buildings and cathedral ruins</li>
        <li>Enjoying a post-round drink at the famous Jigger Inn</li>
      </ul>
      
      <p>For the complete experience, stay at the Old Course Hotel or one of the historic inns in town, allowing you to fully immerse yourself in the atmosphere of this special place.</p>
    `
  },
  {
    id: 3,
    slug: 'pine-valley-americas-greatest-test',
    title: 'Pine Valley Golf Club: America\'s Greatest Test',
    date: 'April 11, 2025',
    image: 'https://images.pexels.com/photos/6859552/pexels-photo-6859552.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Discover what makes Pine Valley consistently rank as one of the world\'s top courses, with insights into its unique challenges and stunning design.',
    author: 'Lisa Chen',
    authorTitle: 'Golf Architecture Expert',
    content: `<p>Content coming soon</p>`
  },
  {
    id: 4,
    slug: 'mountain-view-golfing-in-the-clouds',
    title: 'Mountain View Golf Resort: Golfing in the Clouds',
    date: 'April 4, 2025',
    image: 'https://images.pexels.com/photos/1473050/pexels-photo-1473050.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Our comprehensive review of Mountain View\'s challenging layout, spectacular views, and world-class amenities.',
    author: 'Robert Taylor',
    authorTitle: 'Travel Golf Writer',
    content: `<p>Content coming soon</p>`
  }
];

/**
 * Individual Course Review page component
 */
const CourseReviewPage: React.FC = () => {
  const { slug } = useParams();
  const review = courseReviews.find(review => review.slug === slug);
  
  if (!review) {
    return (
      <PageLayout 
        title="Review Not Found" 
        backgroundImage="https://images.pexels.com/photos/6573288/pexels-photo-6573288.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      >
        <div className="blog-post">
          <h2>Sorry, the course review you're looking for couldn't be found.</h2>
          <p>Please return to our <Link to="/course-reviews">Course Reviews page</Link> to find more reviews.</p>
        </div>
      </PageLayout>
    );
  }
  
  return (
    <PageLayout 
      title={review.title} 
      backgroundImage={review.image}
    >
      <div className="blog-post">
        <div className="blog-post-header">
          <div className="blog-post-meta">
            <span>{review.date}</span>
            <span>|</span>
            <span>By {review.author}, {review.authorTitle}</span>
          </div>
        </div>
        
        <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: review.content }} />
        
        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <Link to="/course-reviews" className="blog-post-link">← Back to All Course Reviews</Link>
        </div>
      </div>
      
      <section style={{ marginTop: '3rem' }}>
        <h2>Related Reviews</h2>
        <div className="blog-posts">
          {courseReviews.filter(r => r.id !== review.id).slice(0, 2).map(relatedReview => (
            <div key={relatedReview.id} className="blog-post-card">
              <div 
                className="blog-post-image"
                style={{ backgroundImage: `url(${relatedReview.image})` }}
              ></div>
              <div className="blog-post-content">
                <div className="blog-post-date">{relatedReview.date}</div>
                <h3 className="blog-post-title">{relatedReview.title}</h3>
                <p className="blog-post-excerpt">{relatedReview.excerpt}</p>
                <Link to={`/course-reviews/${relatedReview.slug}`} className="blog-post-link">
                  Read Full Review →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </PageLayout>
  );
};

export default CourseReviewPage;
